import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { fetchMyAhaEcards } from "../../api/apiClient";

/**
 * The signed-in user's own issued AHA eCards. AHA returns a card code and a claim link at assignment; the student
 * claims the card on AHA's site (survey, then view/download). AHA provides no card PDF through the API.
 * Renders nothing when the user has no issued cards.
 */
export default function AhaEcardsSection() {
  const [cards, setCards] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    fetchMyAhaEcards()
      .then((data) => { if (active) setCards(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, []);

  if (error) {
    return <Alert severity="warning" sx={{ mb: 2 }}>Your AHA eCards could not be loaded right now.</Alert>;
  }
  // Most students have no AHA card, so loading is announced to screen readers only (no visual flicker).
  if (cards === null) {
    return (
      <Box role="status" sx={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
        Loading your AHA eCards…
      </Box>
    );
  }
  if (cards.length === 0) return null;

  return (
    <Box data-testid="aha-ecards-section" sx={{ mb: 3 }}>
      <Typography component="h2" sx={{ fontFamily: "'Space Grotesk', 'Rubik', system-ui, sans-serif", fontSize: 17, fontWeight: 600, mb: 1.25 }}>
        AHA eCards <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}>{cards.length}</Box>
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "14px" }}>
        {cards.map((card) => (
          <Box key={card.id} data-testid="aha-ecard" sx={{ p: 2, border: "1px solid var(--color-hairline-cloud, #e5e7eb)", borderLeft: "4px solid var(--color-accent-lime, #c2ef4e)", borderRadius: "12px", bgcolor: "background.paper" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
              <Typography sx={{ fontWeight: 800 }}>{card.productName}</Typography>
              <Chip size="small" color="success" label="Issued by AHA" />
              {card.environment && card.environment !== "PROD" && (
                <Chip size="small" variant="outlined" label={`AHA ${card.environment} (test)`} />
              )}
            </Box>
            {card.classTitle && <Typography variant="body2" color="text.secondary">{card.classTitle}</Typography>}
            <Typography variant="body2" sx={{ mt: 1 }}>
              eCard code: <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 700 }}>{card.eCardCode}</Box>
            </Typography>
            {card.issuedAt && (
              <Typography variant="caption" color="text.secondary">
                Issued {new Date(card.issuedAt).toLocaleDateString()}
              </Typography>
            )}
            <Box sx={{ mt: 1.5 }}>
              {card.claimUrl ? (
                <Button variant="contained" size="small" endIcon={<OpenInNewIcon />}
                  href={card.claimUrl} target="_blank" rel="noopener noreferrer">
                  Claim your card
                </Button>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Claim link not available. Use your eCard code at heart.org to claim this card.
                </Typography>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              Claiming opens AHA's site, where you complete a short survey and can then view and download your card.
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
